import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import { useSelector } from 'react-redux';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { getGolfSettingsByBranchId, updateGolfSettings } from '../../../api/golf';
import { useToast } from "use-toast-mui";
import { useTranslation } from "react-i18next";
import ReservationEnabledPanel from './ReservationEnabledPanel';
import ReservationDisabledPanel from './ReservationDisabledPanel';

// 메뉴 버튼 컴포넌트
const MenuButton = ({ label, isSelected, onClick, icon }) => (
  <Button
    onClick={onClick}
    sx={{
      width: '320px',
      padding: '16px',
      borderRadius: '8px',
      justifyContent: 'space-between',
      textTransform: 'none',
      backgroundColor: isSelected ? '#0F7F85' : 'transparent',
      color: isSelected ? '#FFFFFF' : '#A6A6A6',
      '&:hover': {
        backgroundColor: isSelected ? '#0F7F85' : 'rgba(0, 0, 0, 0.04)',
      },
    }}>
    <Typography
      sx={{
        fontSize: '20px',
        fontWeight: 600,
        color: isSelected ? '#FFFFFF' : '#A6A6A6',
      }}>
      {label}
    </Typography>
    {icon}
  </Button>
);

const MemberAppSettings = ({ branchId }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);

  // 예약 가능/불가능 선택 상태
  const [reservationEnabled, setReservationEnabled] = useState(true);

  // 사전 예약 설정
  const [advanceReservation, setAdvanceReservation] = useState({
    enabled: true,
    reservationDays: '7',
    timeSlotOption: 'branchHours', // 'branchHours' | 'custom'
    timeUnitMinutes: '10',
    unavailableHours: '1',
  });

  // 실시간 예약 설정
  const [realtimeReservation, setRealtimeReservation] = useState({
    enabled: true,
    timeSlotOption: 'branchHours', // 'branchHours' | 'custom'
  });

  // 숏타임 예약 설정
  const [shortTimeReservation, setShortTimeReservation] = useState({
    enabled: true,
  });

  // 커스텀 시간 설정 데이터
  const [advanceCustomSchedule, setAdvanceCustomSchedule] = useState(null);
  const [realtimeCustomSchedule, setRealtimeCustomSchedule] = useState(null);

  // 예약 불가능 설정 (DB 컬럼명: SPOT_STATUS_SHOW_YN, SHORT_TIME_STATUS_SHOW_YN)
  const [disabledSettings, setDisabledSettings] = useState({
    spotStatusShowYn: 'Y',
    shortTimeStatusShowYn: 'Y',
  });

  // API에서 설정 데이터 가져오기
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (user?.token && branchId) {
          const response = await getGolfSettingsByBranchId(user.token, branchId);
          if (response) {
            // 예약 가능/불가능 상태 설정
            setReservationEnabled(response.reservationEnabled === 'Y');

            // 사전 예약 설정
            setAdvanceReservation({
              enabled: response.advanceReservationEnabled === 'Y',
              reservationDays: String(response.advanceReservationDays ?? 7),
              timeSlotOption: response.advanceTimeSlotOption === 'CUSTOM' ? 'custom' : 'branchHours',
              timeUnitMinutes: String(response.advanceTimeUnitMinutes ?? 10),
              unavailableHours: String(response.advanceUnavailableTimes ?? 1),
            });

            // 실시간 예약 설정
            setRealtimeReservation({
              enabled: response.realtimeReservationEnabled === 'Y',
              timeSlotOption: response.realtimeTimeSlotOption === 'CUSTOM' ? 'custom' : 'branchHours',
            });

            // 숏타임 예약 설정
            setShortTimeReservation({
              enabled: response.shortTimeReservationEnabled === 'Y',
            });

            // 예약 불가능 설정
            setDisabledSettings({
              spotStatusShowYn: response.spotStatusShowYn ?? 'Y',
              shortTimeStatusShowYn: response.shortTimeStatusShowYn ?? 'Y',
            });

            // 커스텀 시간 설정 로드
            if (response.customTimes && response.customTimes.length > 0) {
              const advanceTimes = response.customTimes.filter(ct => ct.reservationType === 'ADVANCE');
              const realtimeTimes = response.customTimes.filter(ct => ct.reservationType === 'REALTIME');

              if (advanceTimes.length > 0) {
                const advanceSchedule = advanceTimes.map(ct => ({
                  dayOfWeek: ct.dayOfWeek,
                  isAvailable: ct.isAvailable === 'Y',
                  startHour: ct.startTime ? ct.startTime.substring(0, 2) : '09',
                  startMinute: ct.startTime ? ct.startTime.substring(3, 5) : '00',
                  endHour: ct.endTime ? ct.endTime.substring(0, 2) : '22',
                  endMinute: ct.endTime ? ct.endTime.substring(3, 5) : '00',
                }));
                setAdvanceCustomSchedule(advanceSchedule);
              }

              if (realtimeTimes.length > 0) {
                const realtimeSchedule = realtimeTimes.map(ct => ({
                  dayOfWeek: ct.dayOfWeek,
                  isAvailable: ct.isAvailable === 'Y',
                  startHour: ct.startTime ? ct.startTime.substring(0, 2) : '09',
                  startMinute: ct.startTime ? ct.startTime.substring(3, 5) : '00',
                  endHour: ct.endTime ? ct.endTime.substring(0, 2) : '22',
                  endMinute: ct.endTime ? ct.endTime.substring(3, 5) : '00',
                }));
                setRealtimeCustomSchedule(realtimeSchedule);
              }
            }
          }
        }
      } catch (error) {
        console.log('골프 설정 조회 실패', error);
      }
    };
    fetchSettings();
  }, [branchId, user?.token]);

  const handleSave = async () => {
    try {
      if (!user?.token || !branchId) {
        console.log('토큰 또는 branchId가 없습니다');
        return;
      }

      // 사전 예약 커스텀 시간 데이터 변환
      const advanceCustomTimes = advanceCustomSchedule
        ? advanceCustomSchedule.map(day => ({
            dayOfWeek: day.dayOfWeek,
            isAvailable: day.isAvailable ? 'Y' : 'N',
            startTime: `${day.startHour}:${day.startMinute}`,
            endTime: `${day.endHour}:${day.endMinute}`,
          }))
        : null;

      // 실시간 예약 커스텀 시간 데이터 변환
      const realtimeCustomTimes = realtimeCustomSchedule
        ? realtimeCustomSchedule.map(day => ({
            dayOfWeek: day.dayOfWeek,
            isAvailable: day.isAvailable ? 'Y' : 'N',
            startTime: `${day.startHour}:${day.startMinute}`,
            endTime: `${day.endHour}:${day.endMinute}`,
          }))
        : null;

      const requestData = {
        branchId,
        reservationEnabled: reservationEnabled ? 'Y' : 'N',
        advanceReservationEnabled: advanceReservation.enabled ? 'Y' : 'N',
        advanceReservationDays: parseInt(advanceReservation.reservationDays) || 7,
        advanceTimeSlotOption: advanceReservation.timeSlotOption === 'custom' ? 'CUSTOM' : 'BRANCH_TIMES',
        advanceTimeUnitMinutes: parseInt(advanceReservation.timeUnitMinutes) || 10,
        advanceUnavailableTimes: parseInt(advanceReservation.unavailableHours) || 1,
        realtimeReservationEnabled: realtimeReservation.enabled ? 'Y' : 'N',
        realtimeTimeSlotOption: realtimeReservation.timeSlotOption === 'custom' ? 'CUSTOM' : 'BRANCH_TIMES',
        shortTimeReservationEnabled: shortTimeReservation.enabled ? 'Y' : 'N',
        shortTimeYn: shortTimeReservation.available ? 'Y' : 'N',
        advanceCustomTimes,
        realtimeCustomTimes,
        // 예약 불가능 설정
        spotStatusShowYn: disabledSettings.spotStatusShowYn,
        shortTimeStatusShowYn: disabledSettings.shortTimeStatusShowYn,
      };

      const response = await updateGolfSettings(user.token, requestData);
      if (response) {
        toast.success(t('Golf settings updated successfully'));
      }
    } catch (error) {
      console.log('설정 저장 실패', error);
      toast.error(t('Failed to save settings'));
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: '32px', padding: '26px 32px' }}>
      {/* 좌측 메뉴 바 */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #D9D9D9',
          borderRadius: '8px',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          height: 'fit-content',
        }}>
        <MenuButton
          label="회원앱 예약 가능"
          isSelected={reservationEnabled}
          onClick={() => setReservationEnabled(true)}
          icon={<CheckCircleOutlineIcon sx={{ color: reservationEnabled ? '#FFFFFF' : '#A6A6A6' }} />}
        />
        <MenuButton
          label="회원앱 예약 불가능"
          isSelected={!reservationEnabled}
          onClick={() => setReservationEnabled(false)}
          icon={<RemoveCircleOutlineIcon sx={{ color: !reservationEnabled ? '#FFFFFF' : '#A6A6A6' }} />}
        />
      </Box>

      {/* 우측 설정 영역 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', alignItems: 'center', width: '1104px' }}>
        {reservationEnabled ? (
          <ReservationEnabledPanel
            advanceReservation={advanceReservation}
            setAdvanceReservation={setAdvanceReservation}
            realtimeReservation={realtimeReservation}
            setRealtimeReservation={setRealtimeReservation}
            shortTimeReservation={shortTimeReservation}
            setShortTimeReservation={setShortTimeReservation}
            advanceCustomSchedule={advanceCustomSchedule}
            setAdvanceCustomSchedule={setAdvanceCustomSchedule}
            realtimeCustomSchedule={realtimeCustomSchedule}
            setRealtimeCustomSchedule={setRealtimeCustomSchedule}
            onSave={handleSave}
          />
        ) : (
          <ReservationDisabledPanel
            disabledSettings={disabledSettings}
            setDisabledSettings={setDisabledSettings}
            onSave={handleSave}
          />
        )}
      </Box>
    </Box>
  );
};

export default MemberAppSettings;
